import { useState } from "react";
import { useNotify } from "ra-core";
import { Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { pruefe, sb } from "./daten";
import {
  KATEGORIEN,
  VERRECHNET,
  cent,
  type Quelle,
  type Rechnung,
} from "./kern/typen";
import {
  Abschnitt,
  Auswahl,
  Feld,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  heute,
  zahlLesen,
  type TabProps,
} from "./teile";

const useSchreiben = (neuLaden: () => Promise<unknown>) => {
  const notify = useNotify();
  return async (
    aktion: () => PromiseLike<{ error: { message: string } | null }>,
  ) => {
    try {
      pruefe(await aktion());
      await neuLaden();
      return true;
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
      return false;
    }
  };
};

const gesamt = (r: Rechnung) =>
  cent(r.brutto_19) + cent(r.brutto_7) + cent(r.brutto_0);

export const ErfassungTab = (props: TabProps) => (
  <Tabs defaultValue="rechnungen">
    <TabsList>
      <TabsTrigger value="rechnungen">Rechnungen</TabsTrigger>
      <TabsTrigger value="stripe">Stripe</TabsTrigger>
      <TabsTrigger value="belege">Belege und Zeilen</TabsTrigger>
    </TabsList>
    <TabsContent value="rechnungen">
      <Rechnungen {...props} />
    </TabsContent>
    <TabsContent value="stripe">
      <Stripe {...props} />
    </TabsContent>
    <TabsContent value="belege">
      <Belege {...props} />
    </TabsContent>
  </Tabs>
);

const LEER_RECHNUNG = {
  nummer: "",
  kunde: "",
  rechnungsdatum: heute(),
  leistungsdatum: "",
  brutto_19: "",
  brutto_7: "",
  brutto_0: "",
};

const Rechnungen = ({ jahr, daten, basis, neuLaden }: TabProps) => {
  const schreiben = useSchreiben(neuLaden);
  const [f, setF] = useState(LEER_RECHNUNG);
  const [zahlung, setZahlung] = useState<Record<number, string>>({});
  const liste = daten.rechnungen.filter(
    (r) =>
      r.rechnungsdatum.startsWith(`${jahr}-`) ||
      r.bezahlt_am?.startsWith(`${jahr}-`),
  );
  const passende = (r: Rechnung) =>
    daten.buchungen.find(
      (b) =>
        cent(b.betrag) === gesamt(r) &&
        !b.rechnung_id &&
        b.kategorie !== VERRECHNET,
    );

  return (
    <Abschnitt
      titel="Ausgangsrechnungen"
      text={
        basis.jahr.besteuerung === "ist"
          ? "Nach Ist zählt nur der Zahlungseingang."
          : "Nach Soll zählt für die UStVA die Leistung, für die EÜR die Zahlung."
      }
    >
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-7">
        <Feld name="Nummer">
          <Input
            value={f.nummer}
            onChange={(e) => setF({ ...f, nummer: e.target.value })}
          />
        </Feld>
        <Feld name="Kunde">
          <Input
            value={f.kunde}
            onChange={(e) => setF({ ...f, kunde: e.target.value })}
          />
        </Feld>
        <Feld name="Rechnungsdatum">
          <Input
            type="date"
            value={f.rechnungsdatum}
            onChange={(e) => setF({ ...f, rechnungsdatum: e.target.value })}
          />
        </Feld>
        <Feld name="Leistungsdatum">
          <Input
            type="date"
            value={f.leistungsdatum}
            onChange={(e) => setF({ ...f, leistungsdatum: e.target.value })}
          />
        </Feld>
        <Feld name={`Brutto ${basis.jahr.satz_regel} %`}>
          <Input
            inputMode="decimal"
            value={f.brutto_19}
            onChange={(e) => setF({ ...f, brutto_19: e.target.value })}
          />
        </Feld>
        <Feld name={`Brutto ${basis.jahr.satz_ermaessigt} %`}>
          <Input
            inputMode="decimal"
            value={f.brutto_7}
            onChange={(e) => setF({ ...f, brutto_7: e.target.value })}
          />
        </Feld>
        <Feld name="Brutto 0 %">
          <Input
            inputMode="decimal"
            value={f.brutto_0}
            onChange={(e) => setF({ ...f, brutto_0: e.target.value })}
          />
        </Feld>
      </div>
      <Button
        disabled={!f.nummer}
        onClick={async () => {
          const ok = await schreiben(() =>
            sb()
              .from("bh_rechnung")
              .insert({
                ...f,
                leistungsdatum: f.leistungsdatum || null,
                brutto_19: zahlLesen(f.brutto_19),
                brutto_7: zahlLesen(f.brutto_7),
                brutto_0: zahlLesen(f.brutto_0),
              }),
          );
          if (ok) setF(LEER_RECHNUNG);
        }}
      >
        Rechnung anlegen
      </Button>
      <Tabelle
        kopf={["Nummer", "Kunde", "Datum", "Brutto", "Status", ""]}
        rechts={[3]}
      >
        {liste.map((r) => {
          const treffer = r.status === "bezahlt" ? passende(r) : undefined;
          return (
            <tr key={r.id}>
              <td>{r.nummer}</td>
              <td>{r.kunde}</td>
              <td className="whitespace-nowrap">
                {fmtDatum(r.rechnungsdatum)}
              </td>
              <Zahl>{fmtCent(gesamt(r))}</Zahl>
              <td className="whitespace-nowrap">
                {r.status === "bezahlt" ? (
                  <Badge variant="secondary">
                    bezahlt {fmtDatum(r.bezahlt_am)}
                  </Badge>
                ) : (
                  <Badge variant="outline">offen</Badge>
                )}
              </td>
              <td>
                <div className="flex flex-wrap items-center justify-end gap-1">
                  {r.status === "offen" ? (
                    <>
                      <Input
                        type="date"
                        className="w-36"
                        aria-label="Zahlungsdatum"
                        value={zahlung[r.id] ?? heute()}
                        onChange={(e) =>
                          setZahlung({ ...zahlung, [r.id]: e.target.value })
                        }
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          schreiben(() =>
                            sb()
                              .from("bh_rechnung")
                              .update({
                                status: "bezahlt",
                                bezahlt_am: zahlung[r.id] ?? heute(),
                              })
                              .eq("id", r.id),
                          )
                        }
                      >
                        Bezahlt
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        schreiben(() =>
                          sb()
                            .from("bh_rechnung")
                            .update({ status: "offen", bezahlt_am: null })
                            .eq("id", r.id),
                        )
                      }
                    >
                      Zurück auf offen
                    </Button>
                  )}
                  {treffer && (
                    <Button
                      size="sm"
                      variant="outline"
                      title="Der Zahlungseingang zählt dann nicht zusätzlich zur Rechnung"
                      onClick={() =>
                        schreiben(() =>
                          sb()
                            .from("bh_buchung")
                            .update({
                              rechnung_id: r.id,
                              kategorie: VERRECHNET,
                              satz: 0,
                            })
                            .eq("id", treffer.id),
                        )
                      }
                    >
                      Kontozeile {fmtDatum(treffer.datum)} verknüpfen
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Rechnung löschen"
                    onClick={() =>
                      schreiben(() =>
                        sb().from("bh_rechnung").delete().eq("id", r.id),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </td>
            </tr>
          );
        })}
      </Tabelle>
    </Abschnitt>
  );
};

const LEER_STRIPE = {
  datum: heute(),
  beschreibung: "",
  brutto: "",
  gebuehr: "",
  satz: "19",
  beleg: "",
};

const Stripe = ({ daten, neuLaden }: TabProps) => {
  const schreiben = useSchreiben(neuLaden);
  const [f, setF] = useState(LEER_STRIPE);
  return (
    <Abschnitt
      titel="Stripe-Umsätze"
      text="Brutto und Gebühr getrennt. Die Auszahlung auf dem Konto steht als „Verrechnet“ und zählt nicht."
    >
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Feld name="Datum">
          <Input
            type="date"
            value={f.datum}
            onChange={(e) => setF({ ...f, datum: e.target.value })}
          />
        </Feld>
        <Feld name="Beschreibung">
          <Input
            value={f.beschreibung}
            onChange={(e) => setF({ ...f, beschreibung: e.target.value })}
          />
        </Feld>
        <Feld name="Brutto">
          <Input
            inputMode="decimal"
            value={f.brutto}
            onChange={(e) => setF({ ...f, brutto: e.target.value })}
          />
        </Feld>
        <Feld name="Gebühr">
          <Input
            inputMode="decimal"
            value={f.gebuehr}
            onChange={(e) => setF({ ...f, gebuehr: e.target.value })}
          />
        </Feld>
        <Feld name="Satz">
          <Auswahl
            value={f.satz}
            onChange={(e) => setF({ ...f, satz: e.target.value })}
          >
            <option value="19">19 %</option>
            <option value="7">7 %</option>
            <option value="0">0 %</option>
          </Auswahl>
        </Feld>
        <Feld name="Beleg">
          <Input
            value={f.beleg}
            onChange={(e) => setF({ ...f, beleg: e.target.value })}
          />
        </Feld>
      </div>
      <Button
        disabled={!f.brutto}
        onClick={async () => {
          const ok = await schreiben(() =>
            sb()
              .from("bh_stripe_umsatz")
              .insert({
                ...f,
                brutto: zahlLesen(f.brutto),
                gebuehr: zahlLesen(f.gebuehr),
                satz: Number(f.satz),
                beleg: f.beleg || null,
              }),
          );
          if (ok) setF(LEER_STRIPE);
        }}
      >
        Umsatz anlegen
      </Button>
      <Tabelle
        kopf={["Datum", "Beschreibung", "Brutto", "Gebühr", "Satz", ""]}
        rechts={[2, 3, 4]}
      >
        {daten.stripe.map((s) => (
          <tr key={s.id}>
            <td className="whitespace-nowrap">{fmtDatum(s.datum)}</td>
            <td>{s.beschreibung}</td>
            <Zahl>{fmtCent(cent(s.brutto))}</Zahl>
            <Zahl>{fmtCent(cent(s.gebuehr))}</Zahl>
            <Zahl>{s.satz} %</Zahl>
            <td className="text-right">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Umsatz löschen"
                onClick={() =>
                  schreiben(() =>
                    sb().from("bh_stripe_umsatz").delete().eq("id", s.id),
                  )
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </td>
          </tr>
        ))}
      </Tabelle>
    </Abschnitt>
  );
};

const LEER_BELEG = {
  quelle: "einlage" as Quelle,
  konto_id: "",
  datum: heute(),
  betrag: "",
  partner: "",
  text: "",
  beleg: "",
  kategorie: "",
  satz: "19",
};
const QUELLE: Record<string, string> = {
  einlage: "Privat bezahlt (Einlage)",
  bar: "Barbeleg",
  hand: "Kontozeile aus PDF-Auszug",
};

const Belege = ({ daten, neuLaden }: TabProps) => {
  const schreiben = useSchreiben(neuLaden);
  const [f, setF] = useState(LEER_BELEG);
  const hand = f.quelle === "hand";
  const liste = daten.buchungen.filter((b) => b.quelle !== "import");
  return (
    <Abschnitt
      titel="Belege und Zeilen von Hand"
      text="Privat bezahlte Ausgaben, Bargeld und Zeilen aus Auszügen, die nur als PDF vorliegen."
    >
      <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
        <Feld name="Art">
          <Auswahl
            value={f.quelle}
            onChange={(e) => setF({ ...f, quelle: e.target.value as Quelle })}
          >
            {Object.entries(QUELLE).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Auswahl>
        </Feld>
        {hand && (
          <Feld name="Konto">
            <Auswahl
              value={f.konto_id}
              onChange={(e) => setF({ ...f, konto_id: e.target.value })}
            >
              <option value="">bitte wählen</option>
              {daten.konten.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.name}
                </option>
              ))}
            </Auswahl>
          </Feld>
        )}
        <Feld name="Datum">
          <Input
            type="date"
            value={f.datum}
            onChange={(e) => setF({ ...f, datum: e.target.value })}
          />
        </Feld>
        <Feld name={hand ? "Betrag (Ausgabe negativ)" : "Betrag der Ausgabe"}>
          <Input
            inputMode="decimal"
            value={f.betrag}
            onChange={(e) => setF({ ...f, betrag: e.target.value })}
          />
        </Feld>
        <Feld name="Partner">
          <Input
            value={f.partner}
            onChange={(e) => setF({ ...f, partner: e.target.value })}
          />
        </Feld>
        <Feld name="Zweck">
          <Input
            value={f.text}
            onChange={(e) => setF({ ...f, text: e.target.value })}
          />
        </Feld>
        <Feld name="Beleg">
          <Input
            value={f.beleg}
            onChange={(e) => setF({ ...f, beleg: e.target.value })}
          />
        </Feld>
        <Feld name="Kategorie">
          <Auswahl
            value={f.kategorie}
            onChange={(e) => setF({ ...f, kategorie: e.target.value })}
          >
            <option value="">später</option>
            {KATEGORIEN.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </Auswahl>
        </Feld>
        <Feld name="Satz">
          <Auswahl
            value={f.satz}
            onChange={(e) => setF({ ...f, satz: e.target.value })}
          >
            <option value="19">19 %</option>
            <option value="7">7 %</option>
            <option value="0">0 %</option>
          </Auswahl>
        </Feld>
      </div>
      <Button
        disabled={!f.betrag || (hand && !f.konto_id)}
        onClick={async () => {
          const betrag = zahlLesen(f.betrag.replace(/^−/, "-"));
          const ok = await schreiben(() =>
            sb()
              .from("bh_buchung")
              .insert({
                quelle: f.quelle,
                konto_id: hand ? Number(f.konto_id) : null,
                datum: f.datum,
                betrag: hand ? betrag : -Math.abs(betrag),
                partner: f.partner,
                text: f.text,
                beleg: f.beleg || null,
                kategorie: f.kategorie || null,
                satz: f.kategorie ? Number(f.satz) : null,
              }),
          );
          if (ok)
            setF({ ...LEER_BELEG, quelle: f.quelle, konto_id: f.konto_id });
        }}
      >
        Eintragen
      </Button>
      <Tabelle
        kopf={["Datum", "Art", "Partner", "Zweck", "Betrag", ""]}
        rechts={[4]}
      >
        {liste.map((b) => (
          <tr key={b.id}>
            <td className="whitespace-nowrap">{fmtDatum(b.datum)}</td>
            <td>{QUELLE[b.quelle]}</td>
            <td>{b.partner}</td>
            <td>{b.text}</td>
            <Zahl>{fmtCent(cent(b.betrag))}</Zahl>
            <td className="text-right">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Zeile löschen"
                onClick={() =>
                  schreiben(() =>
                    sb().from("bh_buchung").delete().eq("id", b.id),
                  )
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </td>
          </tr>
        ))}
      </Tabelle>
    </Abschnitt>
  );
};
